import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'pams.sidebar-collapsed';

/**
 * Whether the sidebar is an icon rail, remembered across reloads.
 *
 * `localStorage.setItem` is banned by ESLint in `src/lib/**` only — that rule is about
 * credential storage, and a layout preference is neither a credential nor something
 * `lib/` should own. It lives in `hooks/` for that reason.
 *
 * Defaults to EXPANDED. That matters beyond taste: a nav row's accessible name is
 * `sr-only` rather than absent in the rail (see `AppSidebar`), and defaulting to
 * collapsed would make every shell test start in the state that most needs the guard.
 */
export const useSidebarCollapsed = (): readonly [boolean, () => void] => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      // Private browsing, or storage disabled. The shell works fine without the memory.
      return false;
    }
  });

  // Written in an effect rather than inside the updater: StrictMode invokes updaters
  // twice, and a write is a side effect.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // The choice still applies for this session.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((previous) => !previous), []);

  return [collapsed, toggle] as const;
};
